import wretch from "wretch";
import config from "@/config";
import Bugsnag, { NotifiableError } from "@bugsnag/js";
import { TokensType } from "@/types/auth";

export const AuthService = {
    authenticated: false,
    client: wretch(config.BACKEND_URL[import.meta.env.VITE_RELEASE_STAGE as keyof typeof config.BACKEND_URL], {
        credentials: "include",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        }
    }),
    signin: async (username: string, password: string) => {
        return AuthService.client
            .url("/authentication/login/")
            .post({ username: username, password: password })
            .json(data => {
                AuthService.authenticated = true;
                AuthService.set_tokens(data as TokensType);
            });
    },
    signout: async () => {
        return AuthService.client
            .url("/authentication/logout/")
            .get()
            .json(() => {
                AuthService.authenticated = false;
            });
    },
    refresh: async () => {
        return AuthService.client
            .url("/authentication/refresh/")
            .post()
            .json(data => {
                AuthService.authenticated = true;
                AuthService.set_tokens(data as TokensType);
            });
    },
    set_tokens: (tokens: TokensType) => {
        localStorage.setItem("tokens", JSON.stringify(tokens));
    },
    set_view_as_tokens: (tokens: TokensType) => {
        localStorage.setItem("view_as_tokens", JSON.stringify(tokens));
    },
    get_view_as_tokens: () => {
        const tokensRaw = localStorage.getItem("view_as_tokens");
        if (tokensRaw) {
            return JSON.parse(tokensRaw) as TokensType;
        }
        return null;
    },
    remove_view_as_tokens: () => {
        localStorage.removeItem("view_as_tokens");
    },
    is_view_as_active: () => {
        return !!localStorage.getItem("view_as_tokens");
    },
    get_tokens: () => {
        // Priorité au token 'view as' s'il existe
        const viewAsTokensRaw = localStorage.getItem("view_as_tokens");
        if (viewAsTokensRaw) {
            return JSON.parse(viewAsTokensRaw) as TokensType;
        }
        const tokensRaw = localStorage.getItem("tokens");
        if (tokensRaw) {
            return JSON.parse(tokensRaw) as TokensType;
        }
        return null;
    },
    has_access_expires: () => {
        const tokens = AuthService.get_tokens();
        return tokens ? tokens.access_expires <= Date.now() / 1000 : true;
    },
    has_refresh_expires: () => {
        const tokens = AuthService.get_tokens();
        return tokens ? tokens.refresh_expires <= Date.now() / 1000 : true;
    },
    getValidAccessToken: async () => {
        const tokens = AuthService.get_tokens();
        const hasRefreshExpires = AuthService.has_refresh_expires();

        if (!tokens) {
            AuthService.authenticated = !hasRefreshExpires;
            return null;
        }

        // Si le token d'accès est encore valide, le retourner directement
        if (!AuthService.has_access_expires()) {
            AuthService.authenticated = true;
            return tokens.access;
        }

        // Si le refresh token est expiré, impossible de rafraîchir
        if (hasRefreshExpires) {
            AuthService.authenticated = false;
            return null;
        }

        try {
            // Tenter de rafraîchir le token
            await AuthService.refresh();
            const newTokens = AuthService.get_tokens();
            AuthService.authenticated = true;
            return newTokens ? newTokens.access : null;
        } catch (error) {
            Bugsnag.notify(
                error as NotifiableError,
                (event) => {
                    event.context = "AuthService > getValidAccessToken";
                    event.severity = "error";
                    event.addMetadata("operation", { name: "getValidAccessToken" });
                    event.addMetadata("object", { error: String(error) });
                }
            );

            AuthService.authenticated = false;
            return null;
        } finally {
            // Reset the context and metadata
            Bugsnag.setContext("");
            Bugsnag.clearMetadata("AuthService");
        }
    }
};
